/**
 * What a creator just picked for their cover.
 *
 * The studio offers *one* action — "a photo or a video" — because a creator with
 * a nice picture and a creator with a nice clip should not have to know which of
 * two buttons is theirs before they know what is in their library. So the app has
 * to answer a question the picker does not always answer itself: this asset, is
 * it a picture or a film?
 *
 * `expo-image-picker` reports `type`, and it is documented as *nullable* — "rare
 * but can happen with some Android ContentProviders" — so the answer cannot rest
 * on it alone. Two facts distinguish the two reliably from what is left: only a
 * video carries a duration, and only a video arrives as a video container or a
 * `video/*` type. Everything else is treated as a picture, which is the safer
 * wrong answer: a still renders immediately on the stage, so a mistake shows up
 * in front of the creator rather than silently, whereas a picture mistaken for a
 * film renders as a black rectangle with no explanation.
 *
 * Pure, so it can be pinned down without a photo library.
 */

export type CoverPickKind = 'image' | 'video'

/** The parts of an `ImagePickerAsset` this decision reads. */
export type CoverPickAsset = {
  type?: 'image' | 'video' | 'livePhoto' | 'pairedVideo' | null
  mimeType?: string | null
  fileName?: string | null
  uri?: string | null
  /** Milliseconds, and only for media that has a running time. */
  duration?: number | null
}

const VIDEO_EXTENSIONS = /\.(mp4|mov|m4v|webm|mkv|avi|3gp)$/i

export function coverPickKind(asset: CoverPickAsset): CoverPickKind {
  // The picker's own word first — it is the only field that is never a guess. A
  // live photo is a picture with a video attached, so the still is the cover;
  // its `pairedVideo` half is a film, and that is what a cover film is.
  if (asset.type === 'video' || asset.type === 'pairedVideo') return 'video'
  if (asset.type === 'image' || asset.type === 'livePhoto') return 'image'

  // `type` was absent, so it has to be inferred from the asset itself.
  if (typeof asset.duration === 'number' && asset.duration > 0) return 'video'
  if (asset.mimeType?.startsWith('video/')) return 'video'
  const name = asset.fileName || asset.uri || ''
  if (VIDEO_EXTENSIONS.test(name)) return 'video'
  return 'image'
}
